import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { clsx } from 'clsx'

const {
  Badge,
  Card,
  FieldDescription,
  FieldLabel,
  FieldRoot,
  Input,
  Switch,
  ToggleGroup,
  ToggleGroupItem
} = Base

export const SettingsCard: React.FC<{
  title: string
  description?: ReactNode
  children: ReactNode
}> = ({ title, description, children }) => (
  <Card variant="flat" className="flex flex-col gap-4 p-5">
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium text-neutral-900">{title}</p>
      {description ? (
        <p className="text-xs text-neutral-600">{description}</p>
      ) : null}
    </div>
    {children}
  </Card>
)

// A titled group of panels, for settings too many for one card.
export const SettingsSection: React.FC<{
  title: string
  description?: ReactNode
  children: ReactNode
}> = ({ title, description, children }) => (
  <section className="flex flex-col gap-3">
    <div className="flex flex-col gap-0.5">
      <h3 className="m-0 text-sm font-medium text-neutral-900">{title}</h3>
      {description ? (
        <p className="m-0 text-xs text-neutral-600">{description}</p>
      ) : null}
    </div>
    {children}
  </section>
)

export const SettingsPanel: React.FC<{
  className?: string
  children: ReactNode
}> = ({ className, children }) => (
  <Card variant="flat" className={clsx('flex flex-col gap-4 p-5', className)}>
    {children}
  </Card>
)

// A setting only REVER sees: the badge beside its label, and a note under it.
export const ReverOnlyBadge = () => {
  const { t } = useTranslation()
  return <Badge tone="info">{t('group_settings.rever_only')}</Badge>
}

const FieldTitle: React.FC<{ label: string; reverOnly?: boolean }> = ({
  label,
  reverOnly
}) =>
  reverOnly ? (
    <div className="flex items-center gap-2">
      <FieldLabel>{label}</FieldLabel>
      <ReverOnlyBadge />
    </div>
  ) : (
    <FieldLabel>{label}</FieldLabel>
  )

const ReverOnlyNote: React.FC<{ reverOnly?: boolean }> = ({ reverOnly }) => {
  const { t } = useTranslation()
  return reverOnly ? (
    <FieldDescription>{t('group_settings.rever_only_note')}</FieldDescription>
  ) : null
}

export const SwitchRow: React.FC<{
  label: string
  description?: string
  reverOnly?: boolean
  checked: boolean
  onChange: (checked: boolean) => void
  readOnly: boolean
}> = ({ label, description, reverOnly, checked, onChange, readOnly }) => (
  <FieldRoot className="flex-row items-center justify-between gap-4">
    <div className="flex flex-col gap-0.5">
      <FieldTitle label={label} reverOnly={reverOnly} />
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <ReverOnlyNote reverOnly={reverOnly} />
    </div>
    <Switch checked={checked} disabled={readOnly} onCheckedChange={onChange} />
  </FieldRoot>
)

export interface Option<V extends string> {
  value: V
  label: string
  description?: string
  disabled?: boolean
}

// One of a few options, with what the chosen one does below it. With title, the label shows above them.
export function Choice<V extends string>({
  label,
  title,
  description: fallback,
  reverOnly,
  value,
  options,
  onChange,
  readOnly,
  extra
}: {
  label: string
  title?: boolean
  // Shown when the chosen option has no description of its own.
  description?: string
  reverOnly?: boolean
  value: V
  options: Option<V>[]
  onChange: (value: V) => void
  readOnly: boolean
  // Shown beside the options, e.g. the field the chosen one needs.
  extra?: ReactNode
}) {
  const description =
    options.find((o) => o.value === value)?.description ?? fallback
  const group = (
    <ToggleGroup
      variant="button"
      aria-label={label}
      value={[value]}
      disabled={readOnly}
      // Pressing the chosen option again reports no value; the choice stays.
      onValueChange={(next) => {
        if (next.length) onChange(next[0] as V)
      }}
    >
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
  return (
    <FieldRoot>
      {title ? <FieldTitle label={label} reverOnly={reverOnly} /> : null}
      {extra ? (
        <div className="flex flex-wrap items-center gap-2">
          {group}
          {extra}
        </div>
      ) : (
        group
      )}
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <ReverOnlyNote reverOnly={reverOnly} />
    </FieldRoot>
  )
}

export const NumberField: React.FC<{
  label: string
  description?: string
  value: number
  step?: number
  onChange: (value: number) => void
  readOnly: boolean
}> = ({ label, description, value, step = 0.01, onChange, readOnly }) => (
  <FieldRoot className="w-48">
    <FieldLabel>{label}</FieldLabel>
    <Input
      type="number"
      min={0}
      step={step}
      disabled={readOnly}
      value={value}
      onChange={(e) => onChange(Number(e.currentTarget.value))}
    />
    {description ? <FieldDescription>{description}</FieldDescription> : null}
  </FieldRoot>
)
